import React, { useState } from 'react';
import CredentialsForm from './CredentialsForm';
import { Dialog, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../../../configs/Api';
import { getHeaders } from '../../../middleware/Api';
import EmailPreview from './EmailPreview';

export interface Props {
  open: boolean;
  handleClose: () => void;
  sendAlert: Function;
  organizationId: string;
  onSuccess?: (data: any) => void;
}

enum FlowStage {
  Form = 'form',
  EmailTemplate = 'emailTemplate',
}

const CredentialsFlow: React.FC<Props> = ({
  handleClose,
  open,
  sendAlert,
  organizationId,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [credentials, setCredentials] = useState<any>({});
  const [stage, setStage] = useState<FlowStage>(FlowStage.Form);

  const handleFormSubmit = async ({
    username,
    password,
    type,
  }: {
    username: string;
    password: string;
    type: string;
  }) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}credentials`, {
        method: 'POST',
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, organizationId, type, password }),
      });
      const data = await response.json();
      if (data.type !== 'data') {
        throw new Error(data.payload);
      }
      if (!data.payload?.credential || !data.payload?.integration) {
        throw new Error('Server could not create credential');
      }
      setStage(FlowStage.EmailTemplate);
      onSuccess?.(data.payload);
    } catch (error: any) {
      sendAlert('Failed to create credentials: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const title = function (): string {
    switch (stage) {
      case FlowStage.Form:
        return 'Create HRIS Integration.';
      case FlowStage.EmailTemplate:
        return 'Your Integration Has Been Added!';
      default:
        return 'Error Occurred, Contact Tools Team';
    }
  };
  const renderContent = function (): React.ReactNode {
    switch (stage) {
      case FlowStage.Form:
        return (
          <CredentialsForm
            onSubmit={handleFormSubmit}
            isLoading={isLoading}
            handleClose={handleClose}
          />
        );
      case FlowStage.EmailTemplate:
        return (
          <EmailPreview
            username={credentials.username}
            password={credentials.password}
            onConfirm={() => {
              setStage(FlowStage.Form);
              handleClose();
            }}
          />
        );
      default:
        return 'Error';
    }
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>
        {title()}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {renderContent()}
    </Dialog>
  );
};

export default CredentialsFlow;
